import moment from 'moment';
import { SWRConfiguration } from 'swr';

import { UseSWROutput, useSWR } from '~/hooks/useSWR';
import { fetchFromOldApi } from '~/services/Http';

export type EmployeeShortResponse = {
  fEmpId: number;
  fEmpStatus: string;
  fEmpFirstName: string;
  fEmpLastName: string;
  fEmpGivenNames: string;
  fEmpPersonNumber: string;
  fEmpEmplId: number;
  fEmplStartDate: string; // Date
  fEmpManagerId: number;
  fEmpUnit: number;
  fEmpPosition: string;
  fEmpProfPictureId: string;
  fEmpPhoneWork: string;
  fEmpPhoneMob: string;
  fEmpEmailWork: string;
  fEmpHasAccount: boolean;
};

export type SelectedEmployeeData = {
  employeeId: number;
  status: string;
  firstName: string;
  lastName: string;
  givenNames: string;
  personNumber: string;
  employmentId: number;
  startDate: Date;
  managerId: number;
  unit: number;
  position: string;
  profilePictureId: string;
  phoneWork: string;
  phoneMob: string;
  emailWork: string;
  hasAccount: boolean;
};

const convertEmployeeShortResponseToSelectedEmployeeData = (
  response: EmployeeShortResponse,
): SelectedEmployeeData => {
  const {
    fEmpId,
    fEmpStatus,
    fEmpFirstName,
    fEmpLastName,
    fEmpGivenNames,
    fEmpPersonNumber,
    fEmpEmplId,
    fEmplStartDate,
    fEmpManagerId,
    fEmpUnit,
    fEmpPosition,
    fEmpProfPictureId,
    fEmpPhoneWork,
    fEmpPhoneMob,
    fEmpEmailWork,
    fEmpHasAccount,
  } = response;
  return {
    employeeId: fEmpId,
    status: fEmpStatus,
    firstName: fEmpFirstName,
    lastName: fEmpLastName,
    givenNames: fEmpGivenNames,
    personNumber: fEmpPersonNumber,
    employmentId: fEmpEmplId,
    startDate: moment(fEmplStartDate).toDate(),
    managerId: fEmpManagerId,
    unit: fEmpUnit,
    position: fEmpPosition,
    profilePictureId: fEmpProfPictureId,
    phoneWork: fEmpPhoneWork,
    phoneMob: fEmpPhoneMob,
    emailWork: fEmpEmailWork,
    hasAccount: fEmpHasAccount,
  };
};

export const getEmployeeDataShort = (
  empId: number | string,
): Promise<EmployeeShortResponse[]> =>
  fetchFromOldApi(`/json/employee/${empId}/full?format=SHORT`).then((r) =>
    r.json(),
  );

const SWR_KEY_GET_SELECTED_EMPLOYEE_DATA = 'useGetSelectedEmployeeData';

export const useGetSelectedEmployeeData = (
  props: {
    employeeId?: number;
  },
  swrConfig?: SWRConfiguration<SelectedEmployeeData>,
): UseSWROutput<SelectedEmployeeData> => {
  const { employeeId } = props;

  const defaultConfig = {
    revalidateOnFocus: false,
    refreshInterval: 0,
  };

  return useSWR(
    !!employeeId ? [SWR_KEY_GET_SELECTED_EMPLOYEE_DATA, employeeId] : null,
    ([key_, employeeId]) =>
      getEmployeeDataShort(employeeId).then((result) =>
        convertEmployeeShortResponseToSelectedEmployeeData(result[0]),
      ),
    { ...defaultConfig, ...swrConfig },
  );
};
